import { Fragment, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { BREADCRUMB_MAP } from '@/config/breadcrumbs';

/**
 * Breadcrumbs — builds crumb trail from the current pathname using BREADCRUMB_MAP.
 * Works with the current BrowserRouter (no data router migration needed).
 * Hidden when there's only 1 crumb (i.e. at a root-level page).
 */
export function Breadcrumbs() {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const crumbs = useMemo(() => {
    // Build path segments: /admin/users → ['/admin', '/admin/users']
    const segments = pathname.split('/').filter(Boolean);
    const paths: { path: string; label: string }[] = [];

    let accumulated = '';
    for (const seg of segments) {
      accumulated += '/' + seg;
      const labelKey = BREADCRUMB_MAP[accumulated];
      if (labelKey) {
        paths.push({ path: accumulated, label: t(labelKey) });
      }
    }
    return paths;
  }, [pathname, t]);

  // Don't render if there's 0 or 1 crumb (no trail to show)
  if (crumbs.length <= 1) return null;

  return (
    <nav aria-label="breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 mb-4">
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <Fragment key={crumb.path}>
            {i > 0 && <ChevronRight size={10} strokeWidth={3} className="text-slate-400 rtl:rotate-180" />}
            {isLast ? (
              <span className="font-medium text-slate-700">{crumb.label}</span>
            ) : (
              <Link to={crumb.path} className="hover:text-primary-600 transition-colors">
                {crumb.label}
              </Link>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
